import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "./Modal.js";
import { getSession } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import { ImpersonationPicker } from "../admin/ImpersonationPicker.js";
import { usePreferences } from "../../preferences/usePreferences.js";
import { useAuth } from "../../auth/AuthProvider.js";
import { useSignedInAccount } from "../../auth/useSignedInAccount.js";
import { ListRow } from "../molecules/ListRow.js";
import {
  GRID_SIZE_MAX,
  GRID_SIZE_MIN,
  GRID_SIZE_STEP,
  useGridSettings,
} from "../../preferences/GridSettingsProvider.js";
import { useToast } from "./ToastProvider.js";
import { getCreditBalance } from "../../api/queries.js";
import { CreditHistory } from "./CreditHistory.js";
import { CreditTopUpPicker } from "../admin/CreditTopUpPicker.js";
import { TranslationRewards } from "./TranslationRewards.js";
import { TranslationModerationPicker } from "../admin/TranslationModerationPicker.js";
import { Button, Icon, isLanguageCode, type MessageKey, Select, SUPPORTED_LANGUAGES, useLanguage, useT, useTheme } from "@mmm/ui";
export type SettingsSection = "account" | "appearance" | "study" | "credits" | "earn" | "data" | "admin";
const SETTINGS_SECTIONS: Array<{ id: SettingsSection; key: MessageKey }> = [
  { id: "account", key: "settings.accountFallbackName" },
  { id: "appearance", key: "settings.section.appearance" },
  { id: "study", key: "settings.section.study" },
  { id: "credits", key: "settings.section.credits" },
  { id: "earn", key: "settings.section.earn" },
  { id: "data", key: "settings.section.data" },
  { id: "admin", key: "settings.section.admin" },
];

/**
 * Account block first (whose session this is, with Sign Out scoped to it),
 * settings below, and Close as the dialog's own dismiss — sign-out and close
 * are very different actions and never share an action row.
 */
export function SettingsModal({ onClose, initialSection = "account" }: { onClose: () => void; initialSection?: SettingsSection }) {
  const { themeMode, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { signOut } = useAuth();
  const account = useSignedInAccount();
  const navigate = useNavigate();
  const pushToast = useToast();
  const { gridSize, setGridSize } = useGridSettings();
  const sessionQuery = useQuery(getSession, undefined);
  const creditsQuery = useQuery(getCreditBalance, undefined);
  const { save: savePreferences } = usePreferences();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [translationModerationOpen, setTranslationModerationOpen] = useState(false);
  const [section, setSection] = useState<SettingsSection>(initialSection);
  const t = useT();

  // Gated on the REAL identity, so the entry point stays reachable while
  // impersonating an ordinary user — otherwise switching to another account
  // would need a sign-out to undo.
  const isSuperadmin = sessionQuery.data?.actor.role === "superadmin";

  if (pickerOpen) {
    return (
      <ImpersonationPicker
        onClose={() => setPickerOpen(false)}
        onImpersonated={() => {
          setPickerOpen(false);
          onClose();
        }}
      />
    );
  }
  if (topUpOpen) return <CreditTopUpPicker onClose={() => setTopUpOpen(false)} />;
  if (translationModerationOpen) return <TranslationModerationPicker onClose={() => setTranslationModerationOpen(false)} />;

  return (
    <Modal onClose={onClose} width="min(960px, calc(100vw - 32px))" className="mmm-settings-modal">
      <h2>{t("settings.title")}</h2>
      <div className="mmm-settings-workspace">
        <nav className="mmm-settings-sections" aria-label={t("settings.title")}>
          {SETTINGS_SECTIONS.filter((item) => item.id !== "admin" || isSuperadmin).map((item) => <button key={item.id} type="button" aria-current={section === item.id ? "page" : undefined} className={section === item.id ? "is-active" : ""} onClick={() => setSection(item.id)}>{t(item.key)}</button>)}
        </nav>
        <div className="mmm-settings-section-content">
      <div className="mmm-settings-list">
        {section === "account" && <>
        <ListRow
          className="mmm-settings-row mmm-settings-account-row"
          leading={
            <span className="mmm-sidebar-avatar mmm-settings-account-avatar">
              {account?.initials ?? "?"}
            </span>
          }
          title={
            <span className="mmm-settings-account-name">
              {account?.name ?? t("settings.accountFallbackName")}
            </span>
          }
          meta={
            <span className="mmm-settings-account-email">
              {account?.email || t("settings.signedIn")}
            </span>
          }
          trailing={
            <Button variant="secondary" className="mmm-settings-signout-btn" onClick={() => void signOut()}>
              {t("settings.signOut")}
            </Button>
          }
        />
        </>}

        {section === "appearance" && <>
        <label className="mmm-settings-row mmm-settings-control-row">
          <span className="mmm-settings-row-label">{t("settings.darkMode")}</span>
          <input
            type="checkbox"
            checked={themeMode === "dark"}
            onChange={toggleTheme}
            className="mmm-input-accent"
          />
        </label>
        </>}

        {section === "study" && <>
        <label className="mmm-settings-row mmm-settings-control-row">
          <span className="mmm-settings-row-label">{t("maps.gridSize")}</span>
          <span className="mmm-settings-range-control">
            <input
              type="range"
              min={GRID_SIZE_MIN}
              max={GRID_SIZE_MAX}
              step={GRID_SIZE_STEP}
              value={gridSize}
              onChange={(event) => setGridSize(Number(event.target.value))}
            />
            <span className="mmm-settings-range-value">
              {t("maps.pixelValue", { value: gridSize })}
            </span>
          </span>
        </label>
        </>}

        {section === "appearance" && <>
        {/* Each option shows its language's own native name — a speaker looking
            for their language must be able to find it regardless of the UI's
            current language, so these are deliberately untranslated. */}
        <label className="mmm-settings-row mmm-settings-control-row">
          <span className="mmm-settings-row-label">{t("settings.language")}</span>
          <Select
            value={language}
            onChange={(event) => {
              const code = event.target.value;
              if (!isLanguageCode(code)) return;
              void setLanguage(code).then((ok) => {
                // Persist only what actually applied — storing a language whose
                // catalog failed to load would hand the user a broken UI on
                // their next sign-in, on every device.
                if (ok) void savePreferences({ language: code });
                if (!ok) {
                  pushToast({
                    variant: "error",
                    title: t("errors.somethingWentWrong"),
                    message: t("settings.languageLoadFailed"),
                  });
                }
              });
            }}
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName}
              </option>
            ))}
          </Select>
        </label>
        </>}

        {section === "data" && <ListRow
          className="mmm-settings-row mmm-settings-action-row"
          title={t("inventory.title")}
          meta={t("inventory.meta")}
          trailing={
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                void navigate("/inventory");
              }}
            >
              {t("common.open")}
            </Button>
          }
        />}

        {/* Superadmin only. `listUsers` is authorized server-side too, so a
            stale/incorrect role here exposes nothing. */}
        {section === "admin" && isSuperadmin && (
          <ListRow
            className="mmm-settings-row mmm-settings-action-row"
            title={t("admin.impersonateTitle")}
            meta={t("admin.impersonateMeta")}
            trailing={
              <Button variant="secondary" onClick={() => setPickerOpen(true)}>
                {t("common.open")}
              </Button>
            }
          />
        )}
        {section === "admin" && isSuperadmin && <ListRow
          className="mmm-settings-row mmm-settings-action-row"
          title={t("credits.addCredits")}
          meta={t("credits.addCreditsDescription")}
          trailing={<Button variant="secondary" onClick={() => setTopUpOpen(true)}>{t("common.open")}</Button>}
        />}

        {/* The only route into the deleted-items view — a soft-deleted subject
            or chapter is invisible everywhere else in the app. */}
        {section === "data" && <ListRow
          className="mmm-settings-row mmm-settings-action-row"
          title={t("settings.recentlyDeleted")}
          meta={t("settings.recentlyDeletedMeta")}
          trailing={
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                void navigate("/deleted");
              }}
            >
              {t("common.open")}
            </Button>
          }
        />}
        {section === "credits" && <ListRow
          className="mmm-settings-row mmm-settings-action-row mmm-settings-credit-row"
          leading={<span className="mmm-settings-credit-icon"><Icon name="credit" /></span>}
          title={t("settings.section.credits")}
          meta={creditsQuery.error ? t("errors.serverUnreachable") : undefined}
          trailing={<strong className="mmm-settings-credit-value" aria-live="polite">{creditsQuery.data && !creditsQuery.error ? creditsQuery.data.available : t("credits.unavailable")}</strong>}
        />}
        {section === "credits" && creditsQuery.data && !creditsQuery.error && <>
          <ListRow title={t("credits.reserved")} trailing={new Intl.NumberFormat(language).format(creditsQuery.data.reserved)} />
          <ListRow title={t("credits.lifetimeEarned")} trailing={new Intl.NumberFormat(language).format(creditsQuery.data.lifetimeEarned)} />
          <ListRow title={t("credits.lifetimeSpent")} trailing={new Intl.NumberFormat(language).format(creditsQuery.data.lifetimeSpent)} />
        </>}
        {section === "credits" && <CreditHistory />}
        {section === "credits" && <ListRow
          className="mmm-settings-row mmm-settings-action-row"
          title={t("settings.section.earn")}
          trailing={<Button variant="secondary" onClick={() => setSection("earn")}>{t("settings.section.earn")}</Button>}
        />}
        {section === "earn" && <TranslationRewards />}
        {section === "admin" && isSuperadmin && <ListRow
          className="mmm-settings-row mmm-settings-action-row"
          title={t("translations.manageCampaign")}
          trailing={<Button variant="secondary" onClick={() => setTranslationModerationOpen(true)}>{t("common.open")}</Button>}
        />}
      </div>
        </div>
      </div>

      <div className="mmm-modal-actions">
        <Button variant="primary" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </Modal>
  );
}
